import { useState, useCallback, useEffect } from 'react';

function useTablePagination({
  initialPageSize = 10,
  updateQueryParams,
  getQueryParams,
  onPageChange
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Initialize pagination from URL parameters
  useEffect(() => {
    const params = getQueryParams();

    if (params.page) {
      const pageNum = parseInt(params.page, 10);
      if (!isNaN(pageNum) && pageNum > 0) {
        setCurrentPage(pageNum);
      }
    }

    if (params.pageSize) {
      const pageSizeNum = parseInt(params.pageSize, 10);
      if (!isNaN(pageSizeNum) && pageSizeNum > 0) {
        setPageSize(pageSizeNum);
      }
    }
  }, [getQueryParams]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    updateQueryParams({ page: newPage.toString() }, { replace: true });

    if (onPageChange) {
      onPageChange(newPage);
    }
  }, [updateQueryParams, onPageChange]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    updateQueryParams({
      pageSize: newPageSize.toString(),
      page: '1'
    }, { replace: true });

    if (onPageChange) {
      onPageChange(1);
    }
  }, [updateQueryParams, onPageChange]);

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleFiltersChange = useCallback(() => {
    setCurrentPage(1);
    // Note: URL update is handled by the filtering hook
  }, []);

  const calculatePagination = useCallback((totalItems) => {
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      totalPages,
      startIndex,
      endIndex,
      totalItems
    };
  }, [currentPage, pageSize]);

  const applyPagination = useCallback((data) => {
    if (!data || data.length === 0) {
      return {
        paginatedData: [],
        totalItems: 0,
        totalPages: 0
      };
    }

    const totalItems = data.length;
    const { totalPages, startIndex, endIndex } = calculatePagination(totalItems);
    const paginatedData = data.slice(startIndex, endIndex);

    return {
      paginatedData,
      totalItems,
      totalPages
    };
  }, [calculatePagination]);

  return {
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    resetToFirstPage,
    handleFiltersChange,
    applyPagination,
    calculatePagination
  };
}

export default useTablePagination;
